import { useMemo, useState, Fragment } from "react";
import { formatINR } from "@/lib/constants";
import { Calculator, ChevronDown, ChevronRight, Percent } from "lucide-react";

const rateForRole = (role) => (role === "Lead Specialist" ? 0.01 : 0.02);

export default function Commission({ leads, team }) {
  const [gst, setGst] = useState(18);
  const [open, setOpen] = useState(null);

  const gstClamped = Math.min(100, Math.max(0, Number(gst) || 0));
  const gstFrac = gstClamped / 100;

  const rows = useMemo(() => {
    return team
      .map((m) => {
        const won = leads.filter(
          (l) => l.status === "Deal Won" && l.assigned_rep === m.name
        );
        const invoice = won.reduce((s, l) => s + (Number(l.sales_value) || 0), 0);
        const base = invoice / (1 + gstFrac);
        const rate = rateForRole(m.role);
        const commission = base * rate;
        return { ...m, won, invoice, base, rate, commission };
      })
      .sort((a, b) => b.commission - a.commission);
  }, [team, leads, gstFrac]);

  const totalCommission = rows.reduce((s, r) => s + r.commission, 0);
  const totalInvoice = rows.reduce((s, r) => s + r.invoice, 0);

  return (
    <div className="p-6 sm:p-8 max-w-5xl w-full mx-auto" data-testid="commission-page">
      <header className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.14em] text-blue-600 font-semibold">
          Payouts
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-1">
          Sales commission calculator
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Commission is calculated on the pre-GST value of each won deal, in real time.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold">
            Total invoiced (won)
          </div>
          <div className="font-heading text-2xl font-bold text-slate-900 mt-1">
            {formatINR(totalInvoice)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold">
            Total commission
          </div>
          <div
            data-testid="total-commission"
            className="font-heading text-2xl font-bold text-emerald-700 mt-1"
          >
            {formatINR(totalCommission)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-1.5 flex items-center gap-1.5">
            <Percent size={12} /> GST rate
          </label>
          <div className="flex items-center gap-2">
            <input
              data-testid="gst-input"
              type="number"
              min="0"
              max="100"
              value={gst}
              onChange={(e) => setGst(e.target.value)}
              className="w-24 h-9 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            />
            <span className="text-sm text-slate-500">% deducted before commission</span>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 mb-6 text-xs text-blue-800 flex items-center gap-2">
        <Calculator size={14} />
        Rates: <b>Sales Rep / Manager = 2%</b> · <b>Lead Specialist = 1%</b>. Example: on a ₹118
        invoice at 18% GST, commission is paid on ₹100.
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="commission-table">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-[0.1em]">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Agent</th>
                <th className="text-left font-semibold px-4 py-3">Role</th>
                <th className="text-right font-semibold px-4 py-3">Won deals</th>
                <th className="text-right font-semibold px-4 py-3">Invoice value</th>
                <th className="text-right font-semibold px-4 py-3">Taxable base</th>
                <th className="text-right font-semibold px-4 py-3">Rate</th>
                <th className="text-right font-semibold px-4 py-3">Commission</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-slate-500 py-16">
                    No team members yet. Add reps in Settings and win deals to see commissions.
                  </td>
                </tr>
              )}
              {rows.map((r, idx) => {
                const isOpen = open === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr
                      data-testid={`commission-row-${idx}`}
                      className="row-hover cursor-pointer"
                      onClick={() => setOpen(isOpen ? null : r.id)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {r.won.length > 0 ? (
                            isOpen ? (
                              <ChevronDown size={15} className="text-slate-400" />
                            ) : (
                              <ChevronRight size={15} className="text-slate-400" />
                            )
                          ) : (
                            <span className="w-[15px]" />
                          )}
                          <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                            {r.name.slice(0, 1).toUpperCase()}
                          </div>
                          <span className="font-semibold text-slate-900">{r.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{r.role}</td>
                      <td className="px-4 py-3 text-right text-slate-800">{r.won.length}</td>
                      <td className="px-4 py-3 text-right text-slate-800">
                        {formatINR(r.invoice)}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-800">
                        {formatINR(r.base)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-xs font-semibold px-2 py-1 rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-200">
                          {(r.rate * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-700">
                        {formatINR(r.commission)}
                      </td>
                    </tr>
                    {isOpen &&
                      r.won.map((l, di) => {
                        const base = (Number(l.sales_value) || 0) / (1 + gstFrac);
                        return (
                          <tr key={l.id} className="bg-slate-50/60">
                            <td className="px-4 py-2 pl-14 text-xs text-slate-600" colSpan={3}>
                              {l.company_name} · {l.product}
                            </td>
                            <td className="px-4 py-2 text-right text-xs text-slate-600">
                              {formatINR(l.sales_value || 0)}
                            </td>
                            <td className="px-4 py-2 text-right text-xs text-slate-600">
                              {formatINR(base)}
                            </td>
                            <td className="px-4 py-2 text-right text-xs text-slate-500">
                              {(r.rate * 100).toFixed(0)}%
                            </td>
                            <td className="px-4 py-2 text-right text-xs font-semibold text-emerald-700">
                              {formatINR(base * r.rate)}
                            </td>
                          </tr>
                        );
                      })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
